// packages/app-sdk/src/manifest.ts
import type { CoreEntity } from '@ndrc/core-db/schema';

export type AppId = string;
export type PermissionKey = string;
export type WorkspaceKind = 'student' | 'group' | 'class' | 'activity' | 'demo';

export interface PermissionDef {
  key: PermissionKey;
  label: string;
}

export interface EmitDef {
  type: string;
  version: number;
  journal: { label: string; importance: 'low' | 'normal' | 'high' } | false;
  realtime: boolean;
  classAll: boolean;
}

export interface SubscribeDef {
  type: string;
  handler: string;
  foreign?: true;
  pattern?: string;
  scope?: 'workspace' | 'class';
}

export interface EntityUse {
  entity: CoreEntity;
  /** Champs ouverts étendus par cette app (ADR-008) ; clé = nom de champ. Décision 2 du plan. */
  extends?: Record<string, readonly string[]>;
}

export interface OwnedTable {
  /** Partage limité à une ligne ; exige le constructeur de politiques paired_app (ADR-026). */
  sharing?: 'pair';
  /** Class rows are writable only by teachers; workspace rows follow the current workspace. */
  scope?: 'workspace' | 'class';
  table: string;
  description: string;
}

export interface EntityAction {
  entity: CoreEntity;
  label: string;
  icon: string;
  intent: string;
  route: string;
  when?: { field: string; eq: unknown };
}

export interface EntityPanel {
  entity: CoreEntity;
  title: string;
  component: string;
}

/** Déclaration côté manifeste (référence par nom) ; l'implémentation (`defineTemplateVar`) est
 * ajoutée par le chantier missions, seul consommateur de cette primitive — pas par ce chantier. */
export interface TemplateVarManifestEntry {
  key: string;
  label: string;
  resolver: string;
}

export interface IntentHandler {
  intent: string;
  route: string;
  priority?: number;
}

export interface StudentUi {
  defaultRoute: string;
  routes: ReadonlyArray<{ path: string; title: string }>;
  layout: 'panel' | 'full';
  overlay?: { component: string; mount: 'always' | 'whenOpen'; position: 'dock' | 'modal' };
}

export interface TeacherUi {
  panel: boolean;
  widgets: ReadonlyArray<{ id: string; title: string; size: 'sm' | 'md' | 'lg' }>;
  studentInspector?: boolean;
  overlay?: { component: string };
}

/**
 * Une pièce du dessin de la marque d'une app, sur la grille 24×24 partagée par toute la trousse
 * d'icônes (`@ndrc/ui`).
 *
 * C'est de la **donnée**, jamais du code : un manifeste est chargé par le bureau, par l'API et par
 * le worker, il ne peut donc rien importer de React. `@ndrc/ui` déclare la même forme de son côté
 * et les deux se rejoignent structurellement, sans qu'aucun des deux paquets n'importe l'autre.
 */
export interface ManifestGlyphShape {
  /** Le tracé (`<path d="…">`). */
  d: string;
  /** Rempli plutôt que tracé. Un aplat blanc porte mieux qu'un filet à 23 px. */
  fill?: boolean;
  /** Épaisseur du trait, quand 1,7 est trop lourd (les lignes internes d'une grille). */
  width?: number;
  /** Opacité, pour nuancer une pièce sans introduire une seconde couleur. */
  opacity?: number;
}

export interface AppManifest<Id extends AppId = AppId> {
  workspace?: 'required' | 'none';
  capabilities?: readonly 'interactive'[];
  id: Id;
  name: string;
  description: string;
  /** Nom d'une forme **générique** de la trousse de `@ndrc/ui` (`grid`, `users`, `mail`…). C'est
   * le repli : il sert quand l'app ne dessine pas sa propre marque (voir `glyph`). */
  icon: string;
  /** Couleur de l'app dans la palette **verrouillée** (`docs/design/desktop-reference.md`) : le nom
   * d'une paire (`'teal'`) ou l'une de ses deux valeurs hexadécimales (`'#0F766E'`). Toute autre
   * valeur retombe sur la paire neutre — la palette ne s'élargit pas depuis un manifeste. */
  color: string;
  /**
   * La **marque** de l'app : son dessin à elle, en blanc sur sa couleur, comme l'icône d'un vrai
   * logiciel. Une liste de tracés sur la grille 24×24 ; le cœur la pose sans savoir ce qu'elle
   * représente.
   *
   * C'est ce qui permet à une app ajoutée demain d'avoir sa propre identité visuelle **sans
   * qu'une ligne du cœur ne bouge** (§52 du cahier des charges). Facultatif : une app qui ne
   * déclare rien garde la forme générique de `icon`.
   */
  glyph?: readonly ManifestGlyphShape[];
  version: string;
  /** Version du contrat SDK contre laquelle l'app a été écrite (comparée à ce paquet). */
  sdk: string;
  status: 'dev' | 'beta' | 'stable' | 'deprecated';
  category: string;
  permissions: { uses: readonly PermissionKey[]; defines: readonly PermissionDef[] };
  entities: { uses: readonly EntityUse[]; owns: readonly OwnedTable[] };
  events: { emits: readonly EmitDef[]; subscribes: readonly SubscribeDef[] };
  contributes?: {
    entityActions?: readonly EntityAction[];
    entityPanels?: readonly EntityPanel[];
    templateVars?: readonly TemplateVarManifestEntry[];
  };
  intents?: { handles?: readonly IntentHandler[]; sends?: readonly string[] };
  ui: { student: StudentUi; teacher?: TeacherUi };
  settings?: { scopes: readonly ('institution' | 'class' | 'activity' | 'workspace')[] };
  missions?: {
    actions: readonly string[];
    conditions: readonly string[];
    blocks?: readonly string[];
  };
  dataset?: {
    seeder: string;
    generator?: { schema: string; generate: string };
    answerKeyConsumer?: boolean;
  };
  ai?: { required: false; purposes: readonly string[] };
  migrations: { schema: `app_${Id}`; dir: 'migrations' };
}

// --- Charset de `AppManifest.id` -----------------------------------------------------------
//
// Défaut découvert en revue, reproduit avec `live-chat` ET avec `class` (un mot réservé
// JavaScript) : `id` finit tel quel dans `packages/app-registry/src/registry.generated.*` — à la
// fois comme IDENTIFIANT JavaScript (`import { manifest as <id> } from …`, raccourci d'objet
// `{ <id> }` dans `MANIFESTS`) et comme SUFFIXE de nom de schéma Postgres non guillemeté
// (`app_<id>`). Un tiret ou un mot réservé produit un fichier `.generated.*` syntaxiquement
// invalide qu'AUCUN outil de ce dépôt ne peut ensuite corriger : `pnpm ndrc gen` lui-même ne peut
// plus démarrer (tout `main.ts` importe `commands/db.ts` → `@ndrc/app-registry/manifests` de
// façon statique, et `tsx`/`esbuild` ne peut même plus PARSER ce fichier), et `*.generated.*` ne
// s'édite jamais à la main (règle 7 de CLAUDE.md). D'où une défense à deux niveaux : ce type ici
// (retour immédiat dans l'éditeur pour qui passe par `defineManifest`), et
// `assertValidAppId`/`RESERVED_APP_IDS` dans `tools/ndrc-cli/src/gen/load-apps.ts` (un manifeste
// construit sans `defineManifest` contourne ce type — l'exécution doit refuser aussi, avant toute
// écriture de fichier généré).
//
// Charset retenu : une lettre minuscule ASCII, puis seulement des lettres minuscules, des
// chiffres et des underscores (a-z, 0-9, _) — exactement l'INTERSECTION d'un identifiant Postgres
// non guillemeté valide (`app_<id>` devient un nom de schéma ; Postgres réplie de toute façon en
// minuscules un identifiant non guillemeté, autant l'imposer dès l'écriture) et d'un identifiant
// JavaScript simple (pas de premier caractère `$`/`_`/unicode : inutile ici, et ça garde le
// charset le plus étroit des deux, donc le plus sûr). Les mots réservés JavaScript sont exclus à
// part (`RESERVED_APP_IDS`) : ils passent ce charset (ex. `class`, `let`, `new`) mais restent
// invalides comme IDENTIFIANT (pas comme simple clé de propriété — `{ class: … }` est légal, mais
// `import { manifest as class }` et le raccourci `{ class }` de `MANIFESTS` ne le sont pas).

type Digit = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
type LowerLetter =
  | 'a'
  | 'b'
  | 'c'
  | 'd'
  | 'e'
  | 'f'
  | 'g'
  | 'h'
  | 'i'
  | 'j'
  | 'k'
  | 'l'
  | 'm'
  | 'n'
  | 'o'
  | 'p'
  | 'q'
  | 'r'
  | 's'
  | 't'
  | 'u'
  | 'v'
  | 'w'
  | 'x'
  | 'y'
  | 'z';
type SnakeChar = LowerLetter | Digit | '_';

/** Vrai (au niveau type) ssi chaque caractère de `S` appartient à `SnakeChar` (`''` incluse). */
type IsAllSnakeChars<S extends string> = S extends ''
  ? true
  : S extends `${SnakeChar}${infer Rest}`
    ? IsAllSnakeChars<Rest>
    : false;

/**
 * Mots réservés du langage JavaScript (mots-clés ECMAScript + réservés du mode strict, toujours
 * actif dans un module) susceptibles d'apparaître comme identifiant dans le registre généré.
 * `arguments` et `eval` ajoutés par prudence (liaison interdite en mode strict dans plusieurs
 * productions proches de l'import). **Source de vérité unique** : réexportée en valeur runtime
 * pour que `tools/ndrc-cli/src/gen/load-apps.ts` s'y réfère au lieu de dupliquer la liste — un
 * type seul ne survivrait pas à l'effacement des types à l'exécution.
 */
export const RESERVED_APP_IDS = [
  'break',
  'case',
  'catch',
  'class',
  'const',
  'continue',
  'debugger',
  'default',
  'delete',
  'do',
  'else',
  'enum',
  'export',
  'extends',
  'false',
  'finally',
  'for',
  'function',
  'if',
  'import',
  'in',
  'instanceof',
  'new',
  'null',
  'return',
  'super',
  'switch',
  'this',
  'throw',
  'true',
  'try',
  'typeof',
  'var',
  'void',
  'while',
  'with',
  'yield',
  'let',
  'static',
  'implements',
  'interface',
  'package',
  'private',
  'protected',
  'public',
  'await',
  'arguments',
  'eval',
] as const;

type ReservedAppId = (typeof RESERVED_APP_IDS)[number];

/**
 * Longueur maximale d'un id d'app. Postgres ne rejette jamais un identifiant non guillemeté plus
 * long que `NAMEDATALEN - 1` (63 octets par défaut) : il le TRONQUE silencieusement, sans erreur.
 * `app_<id>` devient un nom de schéma — deux ids distincts qui ne diffèrent qu'après le 63ᵉ
 * caractère produiraient donc le MÊME schéma tronqué, et Postgres les confondrait sans jamais le
 * signaler. 59 = 63 - 4 (le préfixe `app_`) : la marge exacte qui garantit qu'`app_<id>` tient
 * toujours dans la limite, quel que soit `id`. Valeur exportée pour que
 * `tools/ndrc-cli/src/gen/load-apps.ts` s'y réfère au lieu de dupliquer le nombre (même pattern
 * que `RESERVED_APP_IDS` ci-dessus).
 */
export const MAX_APP_ID_LENGTH = 59;

/**
 * Erreur levée par `defineManifest` — voir son commentaire pour le "pourquoi" complet. `code`
 * distingue les deux défauts qu'une contrainte de TYPE seule ne peut pas fermer (une variable
 * annotée `: AppManifest` avant l'appel élargit `id`/`migrations.schema`, et aucune contrainte de
 * type ne borne la longueur d'une chaîne) : `'SCHEMA_MISMATCH'` et `'ID_TOO_LONG'`.
 */
export class ManifestError extends Error {
  readonly code:
    'SCHEMA_MISMATCH' | 'ID_TOO_LONG' | 'INVALID_TEMPLATE_VARIABLE' | 'INVALID_MISSION_CAPABILITY';

  constructor(
    code:
      | 'SCHEMA_MISMATCH'
      | 'ID_TOO_LONG'
      | 'INVALID_TEMPLATE_VARIABLE'
      | 'INVALID_MISSION_CAPABILITY',
    message: string,
  ) {
    super(message);
    this.name = 'ManifestError';
    this.code = code;
  }
}

/**
 * Contrainte de type sur `AppManifest['id']`, appliquée par `defineManifest` ci-dessous : `never`
 * si `S` est vide, ne commence pas par une lettre minuscule ASCII, contient un caractère hors
 * `SnakeChar` après la première lettre, ou vaut un `ReservedAppId` — `S` (préservé tel quel,
 * littéral) sinon. Récursif caractère par caractère : borné à la longueur réelle d'un id d'app
 * (quelques mots), jamais un souci de profondeur d'instanciation TypeScript en pratique.
 */
export type SnakeCaseAppId<S extends string> = S extends ReservedAppId
  ? never
  : S extends `${LowerLetter}${infer Rest}`
    ? IsAllSnakeChars<Rest> extends true
      ? S
      : never
    : never;

/**
 * Identité qui préserve les types littéraux (`id`, tableaux) grâce au générique `const`, et lie
 * `migrations.schema` à `id` ET contraint le charset de `id` (voir `SnakeCaseAppId` ci-dessus).
 *
 * La forme auto-référentielle directe — `defineManifest<const M extends AppManifest<M['id']>>` —
 * échoue : TypeScript rejette la contrainte elle-même avec `TS2344: Type 'M["id"]' does not
 * satisfy the constraint 'string'`, à la définition de la fonction, indépendamment de tout appel
 * (reproduit isolément hors de ce paquet ; détail dans le rapport de tâche). La forme retenue
 * garde `M` bornée par l'`AppManifest` nu (où `Id` vaut `string` par défaut, donc son
 * `migrations.schema` n'impose que le préfixe `app_`), puis ajoute au **paramètre** — pas au
 * générique — une exigence supplémentaire portant sur `M['id']` une fois `M` inférée depuis
 * l'argument réel : `migrations.schema` doit valoir exactement `` `app_${M['id']}` `` ET `id`
 * doit satisfaire `SnakeCaseAppId<M['id']>`. Prouvé par mutation : `schema: 'app_totally_wrong'`
 * sur `id: 'zz_test'` échoue (`TS2322`), `schema: 'no_app_prefix_at_all'` échoue toujours aussi,
 * et `id: 'live-chat'` / `id: 'class'` échouent également (`tests/manifest.test.ts`).
 *
 * Cette contrainte de type a néanmoins une porte ouverte : `const m: AppManifest = { id:
 * 'probe', ..., migrations: { schema: 'app_totalement_different', dir: 'migrations' } };
 * defineManifest(m)`. Annoter la variable AVANT l'appel fixe son type statique à `AppManifest`
 * (donc `Id = string`) indépendamment des valeurs littérales de l'initialiseur : TypeScript infère
 * alors `M = AppManifest`, et la vérification ci-dessus porte sur `M['id']` déjà élargi en
 * `string`, pas sur les littéraux réels `'probe'`/`'app_totalement_different'`. C'est un style
 * d'écriture plausible (fonction fabrique typée, réutilisation d'un manifeste existant via une
 * variable intermédiaire), pas une esquive délibérée — et un contrôle de TYPE ne peut structurellement
 * pas s'en prémunir : il n'existe qu'à la compilation, sur la forme du code, jamais sur les valeurs
 * qui transitent réellement à l'exécution. D'où le contrôle ci-dessous, sur les VALEURS reçues,
 * qui referme la porte quelle que soit la façon dont l'appelant a fait perdre les littéraux au
 * vérificateur de types. La contrainte de type reste utile : elle donne un retour immédiat dans
 * l'éditeur pour le style d'écriture le plus courant (littéral direct ou `as const`) ; c'est ce
 * contrôle à l'exécution qui protège réellement.
 */
export function defineManifest<const M extends AppManifest>(
  m: M & { id: SnakeCaseAppId<M['id']>; migrations: { schema: `app_${M['id']}` } },
): M {
  // `m.id` a le type `M['id'] & SnakeCaseAppId<M['id']>` : une conditionnelle non résolue tant que
  // `M` reste générique dans ce corps de fonction, donc `tsc` ne peut pas prouver qu'elle possède
  // `.length` (TS2339). Coercition explicite en `string` — sans risque : `AppManifest['id']` EST
  // `string` (voir sa définition), et `SnakeCaseAppId<...>`, quand elle ne vaut pas `never`, en est
  // un sous-type littéral.
  const id: string = m.id;
  const expectedSchema = `app_${id}`;
  // Défense à deux niveaux, comme pour `MAX_APP_ID_LENGTH` ci-dessus (fix-7) : ce contrôle vit
  // tout entier dans `defineManifest`, donc un manifeste construit sans jamais l'appeler le
  // contourne entièrement — exactement le style d'écriture décrit dans le commentaire de cette
  // fonction (variable annotée `: AppManifest` avant l'appel). `assertValidSchema` dans
  // `tools/ndrc-cli/src/gen/load-apps.ts` referme cette porte pour toute app qui atteint
  // réellement le générateur, avec son propre code d'erreur (`GenError`, `'INVALID_APP_SCHEMA'`).
  if (m.migrations.schema !== expectedSchema) {
    throw new ManifestError(
      'SCHEMA_MISMATCH',
      `defineManifest("${id}") : migrations.schema doit valoir "${expectedSchema}", reçu ` +
        `"${m.migrations.schema}". Un schéma d'app doit toujours être "app_" suivi exactement de ` +
        `l'id de l'app — corrigez migrations.schema ou id pour qu'ils correspondent.`,
    );
  }
  if (id.length > MAX_APP_ID_LENGTH) {
    throw new ManifestError(
      'ID_TOO_LONG',
      `defineManifest("${id}") : id trop long (${id.length} caractères, ${MAX_APP_ID_LENGTH} ` +
        `maximum). "app_${id}" deviendrait un nom de schéma Postgres : au-delà de 63 octets, ` +
        `Postgres tronque silencieusement un identifiant, sans erreur — deux apps aux ids longs et ` +
        `proches pourraient alors se retrouver, sans le savoir, avec le même schéma tronqué. ` +
        `Raccourcissez l'id.`,
    );
  }
  assertTemplateVariables(m);
  assertMissionCapabilities(m);
  return m;
}

/** Versions explicitement prises en charge. Toute nouvelle version doit être qualifiée. */
export const SUPPORTED_SDK_VERSIONS: readonly string[] = ['0.0.0', '0.0.1'];
export function sdkCompatibilityError(required: string): string | null {
  return SUPPORTED_SDK_VERSIONS.includes(required)
    ? null
    : `SDK incompatible : l’application demande « ${required} ». Versions prises en charge : ${SUPPORTED_SDK_VERSIONS.join(', ')}.`;
}

/** Vérification partagée par le helper et le chargement d’un manifeste brut. */
export function assertTemplateVariables(manifest: AppManifest): void {
  const keys = new Set<string>();
  for (const variable of manifest.contributes?.templateVars ?? []) {
    if (
      !variable.key.startsWith(`${manifest.id}.`) ||
      !/^[a-z][a-z0-9_-]*\.[a-z][a-zA-Z0-9_.]*$/u.test(variable.key) ||
      keys.has(variable.key) ||
      !variable.label.trim() ||
      variable.label.length > 200 ||
      !/^[a-zA-Z_$][a-zA-Z0-9_$]*$/u.test(variable.resolver) ||
      ['__proto__', 'constructor', 'prototype'].includes(variable.resolver)
    )
      throw new ManifestError(
        'INVALID_TEMPLATE_VARIABLE',
        `Variable de l’application ${manifest.id} invalide : ${variable.key}. Vérifiez le préfixe, les doublons, le libellé et le nom de la fonction serveur.`,
      );
    keys.add(variable.key);
  }
}

/** Chaque application possède uniquement ses propres identifiants de mission. */
export function assertMissionCapabilities(manifest: AppManifest): void {
  if (manifest.missions === undefined) return;
  for (const kind of ['actions', 'conditions', 'blocks'] as const) {
    const ids: unknown =
      kind === 'blocks' ? (manifest.missions.blocks ?? []) : manifest.missions[kind];
    if (!Array.isArray(ids) || ids.length > 100)
      throw new ManifestError(
        'INVALID_MISSION_CAPABILITY',
        `L’application ${manifest.id} doit déclarer une liste de ${kind} contenant au plus 100 capacités.`,
      );
    const seen = new Set<string>();
    for (const id of ids) {
      if (
        typeof id !== 'string' ||
        id.length > 160 ||
        !id.startsWith(`${manifest.id}.`) ||
        !/^[a-z][a-z0-9_]*(?:\.[a-z][a-zA-Z0-9_]*)+$/u.test(id) ||
        id.split('.').some((part) => ['__proto__', 'prototype', 'constructor'].includes(part)) ||
        seen.has(id)
      )
        throw new ManifestError(
          'INVALID_MISSION_CAPABILITY',
          `Capacité de mission invalide pour ${manifest.id} (${kind}) : ${String(id)}. Utilisez un identifiant unique préfixé par ${manifest.id}.`,
        );
      seen.add(id);
    }
  }
}
